<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :loading-message="status.spinnerMessage"
    :is-loading="status.isLoading"
    button-location="top"
    @submit="submitForm"
  >
    <template #title>
      Display Sample SMS
    </template>

    <template #top-form>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> SMS Recipient* </label>
        <div class="col-sm-7">
          <dropdown-list
            v-model="smsRecipientName"
            :options="smsTemplateRecipientList"
            @change="getSmsTemplateTopicList"
          />
        </div>
      </div>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> SMS Topic* </label>
        <div class="col-sm-7">
          <dropdown-list
            v-model="smsTopicName"
            :options="smsTemplateTopicList"
            @change="getSmsTemplateList"
          />
        </div>
      </div>
      <div class="row align-items-center">
        <label class="col-sm-3 control-label"> SMS Type* </label>
        <div class="col-sm-7">
          <dropdown-list
            v-model="smsName"
            :options="smsTemplateList"
          />
        </div>
      </div>

      <div class="form-submit-button-group">
        <p-button
          data-test="btn-get-operator-activities"
          type="purple"
          round
          native-type="submit"
          :disabled="status.isLoading || smsTemplateList.length === 0"
        >
          Generate SMS Example
        </p-button>

        <p-button
          type="yellow"
          round
          :disabled="status.isLoading"
          @click="resetForm"
        >
          Reset
        </p-button>
      </div>
    </template>

    <message-html
      class="mt-20"
      :result-html="status.resultMsg"
      has-bottom-line
    />
  </card-container>
</template>

<script setup lang="ts">
import { ref, Ref, reactive } from 'vue';
import { startCase } from 'lodash';

import util from '@/Utils/util';
import { ListType } from '@/models/app';
import { SmsRecipientName, SmsTopicName } from '@/gql/graphql';
import { smsTemplateService } from '@/services/smsTemplates';

const pageId = 'display-sms-example';

const smsTemplateRecipientList: Ref<ListType[]> = ref([]);
const smsRecipientName: Ref<SmsRecipientName | null> = ref(null);
const smsTemplateTopicList: Ref<ListType[]> = ref([]);
const smsTopicName: Ref<SmsTopicName | null> = ref(null);
const smsTemplateList: Ref<ListType[]> = ref([]);
const smsName = ref('');
const status = reactive({
  isLoading: false,
  spinnerMessage: '',
  resultMsg: '',
  isSmsContent: false,
});

const getSmsTemplateRecipientList = async () => {
  status.isLoading = true;
  status.spinnerMessage = 'Loading Template Recipient List...';

  try {
    const smsTemplateRecipientNameList = await smsTemplateService.getSmsTemplateRecipientList();

    smsTemplateRecipientList.value = smsTemplateRecipientNameList.map((smsRecipientName: string, index: number) => {
      return {
        id: index + 1,
        key: smsRecipientName,
        label: startCase(smsRecipientName),
      } as ListType;
    });

    smsRecipientName.value = smsTemplateRecipientList.value[0]?.key as SmsRecipientName;

    await getSmsTemplateTopicList();
  } catch (error) {
    smsRecipientName.value = SmsRecipientName.Driver;
  }

  status.isLoading = false;
};

const getSmsTemplateTopicList = async () => {
  status.isLoading = true;
  status.spinnerMessage = 'Loading Template Topic List...';

  try {
    const smsTemplateTopicNameList = await smsTemplateService.getSmsTemplateTopicList({
      smsRecipientName: smsRecipientName.value!,
    });

    smsTemplateTopicList.value = smsTemplateTopicNameList.map((smsTopicName: string, index: number) => {
      return {
        id: index + 1,
        key: smsTopicName,
        label: startCase(smsTopicName),
      } as ListType;
    });

    smsTopicName.value = smsTemplateTopicList.value[0]?.key as SmsTopicName;

    await getSmsTemplateList();
  } catch (error) {
    await getSmsTemplateRecipientList();
  }

  status.isLoading = false;
};

const getSmsTemplateList = async () => {
  status.isLoading = true;
  status.spinnerMessage = 'Loading Template List...';

  try {
    const smsTemplateNameList = await smsTemplateService.getSmsTemplateList({
      smsRecipientName: smsRecipientName.value!,
      smsTopicName: smsTopicName.value!,
    });

    smsTemplateList.value = smsTemplateNameList.map((smsName: string, index: number) => {
      return {
        id: index + 1,
        key: smsName,
        label: startCase(smsName),
      } as ListType;
    });
    smsName.value = smsTemplateList.value[0]?.key;
  } catch (error) {
    await getSmsTemplateRecipientList();
  }

  status.isLoading = false;
};

const submitForm = async () => {
  status.isSmsContent = false;
  status.resultMsg = '';
  status.spinnerMessage = 'Loading Sms Example...';
  status.isLoading = true;

  try {
    const smsContent = await smsTemplateService.generateSmsExample({
      smsRecipientName: smsRecipientName.value!,
      smsTopicName: smsTopicName.value!,
      smsName: smsName.value,
    });

    status.resultMsg = util.convertTextToHtml(smsContent);
    status.isSmsContent = true;
  } catch (error) {
    status.resultMsg = util.getGraphqlError(error);
  }

  status.isLoading = false;
};

const resetForm = () => {
  status.resultMsg = '';
  status.isSmsContent = false;
  smsRecipientName.value = null;
  smsTopicName.value = null;
  smsName.value = '';
};

util.moveToTop();
getSmsTemplateRecipientList();
</script>

<style lang="scss">
.display-sms-example {
  .result-message {
    text-align: left !important;
    white-space: normal;
    padding-bottom: 10px;
    margin-left: 20px;
  }
}
</style>
